import getApiHeaders from 'utils/getApiHeaders'
import {
  indexNodeLicensesBaseUrl,
  type NodeStorageSummary,
  type NodeStorageUploadResult
} from 'state/services/indexNode/indexNode'

/**
 * Uploads a file in multipart chunks so each request stays under edge limits (e.g. Vercel ~4.5MB).
 */
export async function uploadNodeStorageFileChunked(params: {
  account: string
  accessToken: string
  licenseId: string
  file: File
  relativePath: string
  summary: NodeStorageSummary
}): Promise<NodeStorageUploadResult> {
  const { account, accessToken, licenseId, file, relativePath, summary } = params
  const chunkSize =
    summary.chunkUpload?.recommendedChunkBytes ?? Math.floor(4_194_304 * 0.85)
  if (chunkSize < 1024) {
    throw new Error('Invalid chunk size from server')
  }

  const base = indexNodeLicensesBaseUrl.replace(/\/$/, '')
  const jsonHeaders = getApiHeaders({ account, accessToken })

  const startRes = await fetch(`${base}/licenses/${licenseId}/storage/chunks/start`, {
    method: 'POST',
    headers: {
      ...jsonHeaders,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      relativePath,
      totalSize: file.size,
      chunkSize
    })
  })
  if (!startRes.ok) {
    throw new Error(`Chunk session failed: ${startRes.status}`)
  }
  const { uploadId, expectedChunks } = (await startRes.json()) as {
    uploadId: string
    expectedChunks: number
  }

  const totalChunks = Math.ceil(file.size / chunkSize)
  if (totalChunks !== expectedChunks) {
    throw new Error('Chunk count mismatch')
  }

  for (let i = 0; i < totalChunks; i++) {
    const startB = i * chunkSize
    const endB = Math.min(startB + chunkSize, file.size)
    const blob = file.slice(startB, endB)
    const form = new FormData()
    form.append('chunk', blob, `part-${i}`)
    const headers = getApiHeaders({ account, accessToken })
    delete (headers as Record<string, string>)['Content-Type']
    const putRes = await fetch(
      `${base}/licenses/${licenseId}/storage/chunks/${uploadId}/${i}`,
      {
        method: 'PUT',
        headers: headers as HeadersInit,
        body: form
      }
    )
    if (!putRes.ok) {
      throw new Error(`Chunk ${i} failed: ${putRes.status}`)
    }
  }

  const doneHeaders = getApiHeaders({ account, accessToken })
  const completeRes = await fetch(
    `${base}/licenses/${licenseId}/storage/chunks/${uploadId}/complete`,
    {
      method: 'POST',
      headers: doneHeaders
    }
  )
  if (!completeRes.ok) {
    throw new Error(`Chunk complete failed: ${completeRes.status}`)
  }
  return (await completeRes.json()) as NodeStorageUploadResult
}
